import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../contexts/AuthContext';
import {
  getLesson,
  getProgrammingLesson,
  getLessonResultsAnonymous,
  getLessonResultsByClass,
  getLessonResultsByStudent,
  getProgrammingLessonResultsAnonymous,
  getProgrammingLessonResultsByClass,
  getProgrammingLessonResultsByStudent,
  getClasses,
  getTeacherStudents
} from '../../services/api';
import ErrorCard from '../../components/ErrorCard/ErrorCard';
import './LessonResults.css';

const LessonResults = () => {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { handleRequest, loading } = useApi();
  const [lesson, setLesson] = useState(null);
  const [lessonType, setLessonType] = useState(null); // 'quiz' or 'programming'
  const [results, setResults] = useState([]);
  const [viewMode, setViewMode] = useState('anonymous'); // 'anonymous', 'class', 'student'
  const [selectedClassId, setSelectedClassId] = useState('');
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');

  // Detect lesson type from URL path
  useEffect(() => {
    const path = window.location.pathname;
    const isProgramming = path.includes('/programming-lessons/');
    setLessonType(isProgramming ? 'programming' : 'quiz');
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!lessonType) return;
      
      try {
        const [lessonResponse, classesResponse, studentsResponse] = await Promise.all([
          lessonType === 'programming' 
            ? handleRequest(getProgrammingLesson, lessonId)
            : handleRequest(getLesson, lessonId),
          handleRequest(getClasses).catch(() => ({ data: [] })),
          handleRequest(getTeacherStudents).catch(() => ({ data: [] }))
        ]);
        setLesson(lessonResponse.data);
        setClasses(classesResponse.data || []);
        setStudents(studentsResponse.data || []);
      } catch (error) {
        setErrorMessage('Kunne ikke laste inn leksjonsdata');
      }
    };
    fetchData();
  }, [lessonId, lessonType, handleRequest]);

  useEffect(() => {
    const fetchResults = async () => {
      if (!lesson || !lessonType) return;

      try {
        setErrorMessage('');
        let response;

        if (lessonType === 'programming') {
          if (viewMode === 'anonymous') {
            response = await handleRequest(getProgrammingLessonResultsAnonymous, lessonId);
          } else if (viewMode === 'class' && selectedClassId) {
            response = await handleRequest(getProgrammingLessonResultsByClass, lessonId, selectedClassId);
          } else if (viewMode === 'student' && selectedStudentId) {
            response = await handleRequest(getProgrammingLessonResultsByStudent, lessonId, selectedStudentId);
          } else {
            setResults([]);
            return;
          }
        } else {
          if (viewMode === 'anonymous') {
            response = await handleRequest(getLessonResultsAnonymous, lessonId);
          } else if (viewMode === 'class' && selectedClassId) {
            response = await handleRequest(getLessonResultsByClass, lessonId, selectedClassId);
          } else if (viewMode === 'student' && selectedStudentId) {
            response = await handleRequest(getLessonResultsByStudent, lessonId, selectedStudentId);
          } else {
            setResults([]);
            return;
          }
        }

        setResults(response.data || []);
      } catch (error) {
        setErrorMessage(error.message || 'Kunne ikke laste inn resultater');
        setResults([]);
      }
    };

    fetchResults();
  }, [lessonId, viewMode, selectedClassId, selectedStudentId, lesson, lessonType, handleRequest]);

  const calculateStats = () => {
    if (results.length === 0) {
      if (lessonType === 'programming') {
        return {
          totalSubmissions: 0,
          completedTasks: 0,
          totalTasks: lesson?.tasks?.length || 0,
          completionRate: 0,
          averageAttempts: 0
        };
      }
      return {
        totalAttempts: 0,
        averageScore: 0,
        highestScore: 0,
        lowestScore: 0,
        passRate: 0
      };
    }

    if (lessonType === 'programming') {
      // For programming lessons, group by student and task
      const studentTaskMap = new Map();
      let totalSubmissions = 0;
      let completedCount = 0;
      let totalAttempts = 0;

      results.forEach(submission => {
        totalSubmissions++;
        if (submission.completed) completedCount++;
        totalAttempts += submission.attempts || 0;
        
        const key = `${submission.student?._id || submission.student || 'anonymous'}-${submission.taskNumber}`;
        if (!studentTaskMap.has(key)) {
          studentTaskMap.set(key, submission);
        }
      });

      const uniqueSubmissions = studentTaskMap.size;
      const averageAttempts = uniqueSubmissions > 0 ? Math.round(totalAttempts / uniqueSubmissions) : 0;
      const completionRate = uniqueSubmissions > 0 ? Math.round((completedCount / uniqueSubmissions) * 100) : 0;

      return {
        totalSubmissions: results.length,
        completedTasks: completedCount,
        totalTasks: lesson?.tasks?.length || 0,
        completionRate,
        averageAttempts
      };
    } else {
      // For quiz lessons
      const scores = results.map(r => r.score);
      const averageScore = Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
      const highestScore = Math.max(...scores);
      const lowestScore = Math.min(...scores);
      const passRate = Math.round((results.filter(r => r.score >= 70).length / results.length) * 100);

      return {
        totalAttempts: results.length,
        averageScore,
        highestScore,
        lowestScore,
        passRate
      };
    }
  };

  const stats = calculateStats();

  if (loading || !lesson) {
    return <div className="lesson-results-container">Laster...</div>;
  }

  return (
    <div className="lesson-results-container">
      <header className="results-page-header">
        <div className="header-content">
          <h1>Resultater: {lesson.title}</h1>
          <button className="back-btn" onClick={() => navigate('/dashboard')}>
            Tilbake til oversikten
          </button>
        </div>
      </header>

      <main className="results-page-content">
        {errorMessage && (
          <ErrorCard message={errorMessage} onClose={() => setErrorMessage('')} />
        )}

        <div className="view-mode-selector">
          <h2>Vis resultater etter:</h2>
          <div className="mode-buttons">
            <button
              className={`mode-btn ${viewMode === 'anonymous' ? 'active' : ''}`}
              onClick={() => {
                setViewMode('anonymous');
                setSelectedClassId('');
                setSelectedStudentId('');
              }}
            >
              Anonymt (alle)
            </button>
            <button
              className={`mode-btn ${viewMode === 'class' ? 'active' : ''}`}
              onClick={() => {
                setViewMode('class');
                setSelectedStudentId('');
              }}
            >
              Etter klasse
            </button>
            <button
              className={`mode-btn ${viewMode === 'student' ? 'active' : ''}`}
              onClick={() => {
                setViewMode('student');
                setSelectedClassId('');
              }}
            >
              Etter elev
            </button>
          </div>

          {viewMode === 'class' && (
            <div className="filter-selector">
              <label htmlFor="class-select">Velg klasse:</label>
              <select
                id="class-select"
                value={selectedClassId}
                onChange={(e) => setSelectedClassId(e.target.value)}
                className="filter-select"
              >
                <option value="">-- Velg en klasse --</option>
                {classes.map((classroom) => (
                  <option key={classroom._id || classroom.id} value={classroom._id || classroom.id}>
                    {classroom.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {viewMode === 'student' && (
            <div className="filter-selector">
              <label htmlFor="student-select">Velg elev:</label>
              <select
                id="student-select"
                value={selectedStudentId}
                onChange={(e) => setSelectedStudentId(e.target.value)}
                className="filter-select"
              >
                <option value="">-- Velg en elev --</option>
                {students.map((student) => (
                  <option key={student._id || student.id} value={student._id || student.id}>
                    {student.name} ({student.email})
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="results-stats">
          {lessonType === 'programming' ? (
            <>
              <div className="stat-card">
                <div className="stat-label">Antall innleveringer</div>
                <div className="stat-value">{stats.totalSubmissions || 0}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Fullførte oppgaver</div>
                <div className="stat-value">{stats.completedTasks || 0} / {stats.totalTasks || 0}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Fullføringsgrad</div>
                <div className="stat-value">{stats.completionRate || 0}%</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Gjennomsnittlige forsøk</div>
                <div className="stat-value">{stats.averageAttempts || 0}</div>
              </div>
            </>
          ) : (
            <>
              <div className="stat-card">
                <div className="stat-label">Antall forsøk</div>
                <div className="stat-value">{stats.totalAttempts || 0}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Gjennomsnittlig resultat</div>
                <div className="stat-value">{stats.averageScore || 0}%</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Høyeste resultat</div>
                <div className="stat-value">{stats.highestScore || 0}%</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Laveste resultat</div>
                <div className="stat-value">{stats.lowestScore || 0}%</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Beståttandel (≥70%)</div>
                <div className="stat-value">{stats.passRate || 0}%</div>
              </div>
            </>
          )}
        </div>

        <div className="results-list">
          <h2>Individuelle resultater</h2>
          {results.length === 0 ? (
            <p className="no-results">Ingen resultater funnet for dette filteret.</p>
          ) : (
            <div className="results-grid">
              {results.map((result) => (
                <div key={result._id || result.id} className="result-card">
                  <div className="result-header">
                    {lessonType === 'programming' ? (
                      <>
                        <div className="result-score">
                          <span className="score-value">
                            {result.completed ? '✓ Fullført' : '○ Pågår'}
                          </span>
                          <span className="score-details">
                            Oppgave {result.taskNumber} • {result.attempts || 0} forsøk
                          </span>
                        </div>
                        {result.student && (
                          <div className="result-student">
                            <strong>{result.student.name || result.student.email}</strong>
                          </div>
                        )}
                        <div className="result-date">
                          {new Date(result.createdAt || result.submittedAt).toLocaleString()}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="result-score">
                          <span className="score-value">{result.score}%</span>
                          <span className="score-details">
                            {result.correctCount}/{result.totalQuestions} riktige
                          </span>
                        </div>
                        {result.student && (
                          <div className="result-student">
                            <strong>{result.student.name || result.student.email}</strong>
                          </div>
                        )}
                        <div className="result-date">
                          {new Date(result.createdAt || result.timestamp).toLocaleString()}
                        </div>
                      </>
                    )}
                  </div>
                  {lessonType === 'programming' ? (
                    <div className="result-answers">
                      {result.validationResult && (
                        <div className={`answer-item ${result.validationResult.isValid ? 'correct' : 'incorrect'}`}>
                          <div className="answer-question">
                            <strong>Validering av oppgave {result.taskNumber}</strong>
                          </div>
                          <div className="answer-details">
                            <span className={`answer-badge ${result.validationResult.isValid ? 'correct' : 'incorrect'}`}>
                              {result.validationResult.isValid ? '✓' : '✗'}
                            </span>
                            <span className="answer-text">
                              {result.validationResult.feedback || 'Ingen tilbakemelding tilgjengelig'}
                            </span>
                          </div>
                          {result.validationResult.suggestions && result.validationResult.suggestions.length > 0 && (
                            <div className="suggestions">
                              <strong>Forslag:</strong>
                              <ul>
                                {result.validationResult.suggestions.map((suggestion, idx) => (
                                  <li key={idx}>{suggestion}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="result-answers">
                      {result.answers && result.answers.map((answer, index) => (
                        <div
                          key={index}
                          className={`answer-item ${answer.isCorrect ? 'correct' : 'incorrect'}`}
                        >
                          <div className="answer-question">
                            <strong>Sp{index + 1}:</strong> {answer.question}
                          </div>
                          <div className="answer-details">
                            <span className={`answer-badge ${answer.isCorrect ? 'correct' : 'incorrect'}`}>
                              {answer.isCorrect ? '✓' : '✗'}
                            </span>
                            <span className="answer-text">
                              Svar: {answer.userAnswer || '(Ingen svar)'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default LessonResults;
